/**
 * Support portal configuration
 *
 * Maps an FP's market to the Poolwerx Evolution Platform support portal base,
 * and enumerates the portal links surfaced in the inspector. Adding a new
 * portal link means adding an entry to SUPPORT_PORTAL_LINKS — adding a new
 * market means adding an entry to SUPPORT_PORTAL_BASE.
 */

import React from 'react'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import { FRESHDESK_PORTAL_HOSTS } from '../utils/freshdeskUrls'

/**
 * NOTE on market vocabulary: this module's public `SupportMarket` is `'US'`,
 * the country-style code used by `resolveSupportMarket(country)`. The rest of
 * the app (exception link rows, `constants/markets.ts`, the backend) uses the
 * *market* code `'USA'`. The portal hosts themselves are the single source of
 * truth in `utils/freshdeskUrls.ts` (`'ANZ' | 'USA'`); we map `'US' -> 'USA'`
 * here so there is exactly one place that knows the Evolution Platform domains.
 */
export type SupportMarket = 'ANZ' | 'US'

const HOST_FOR_MARKET: Record<'ANZ' | 'USA', string> = Object.fromEntries(
  Object.entries(FRESHDESK_PORTAL_HOSTS).map(([host, market]) => [market, `https://${host}`]),
) as Record<'ANZ' | 'USA', string>

export const SUPPORT_PORTAL_BASE: Record<SupportMarket, string> = {
  ANZ: HOST_FOR_MARKET.ANZ,
  US: HOST_FOR_MARKET.USA,
}

export interface SupportPortalLink {
  key: string
  label: string
  /** Path appended to the market's portal base URL. */
  path: string
  /** Optional short helper shown under the label. */
  helper?: string
  Icon: React.ComponentType<{ sx?: any; fontSize?: 'small' | 'medium' | 'large' | 'inherit' }>
  /** When true, the resolved label is prefixed with the FP's market (e.g. "ANZ Support Guides"). */
  prefixWithMarket?: boolean
}

export const SUPPORT_PORTAL_LINKS: SupportPortalLink[] = [
  {
    key: 'home',
    label: 'Support Guides',
    path: '/support/home',
    helper: 'Knowledge base & how-to articles',
    Icon: HelpOutlineIcon,
    prefixWithMarket: true,
  },
  {
    key: 'new_ticket',
    label: 'Submit a support ticket',
    path: '/support/tickets/new',
    helper: 'Contact the Evo Support team',
    Icon: ConfirmationNumberIcon,
  },
]

export interface ResolvedSupportLink extends SupportPortalLink {
  url: string
  market: SupportMarket
}

export function resolveSupportMarket(country?: string | null): SupportMarket {
  return country?.toUpperCase() === 'US' ? 'US' : 'ANZ'
}

export function getSupportPortalLinks(country?: string | null): ResolvedSupportLink[] {
  const market = resolveSupportMarket(country)
  const base = SUPPORT_PORTAL_BASE[market]
  return SUPPORT_PORTAL_LINKS.map((link) => ({
    ...link,
    label: link.prefixWithMarket ? `${market} ${link.label}` : link.label,
    url: `${base}${link.path}`,
    market,
  }))
}
